import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Paper,
  Divider as MuiDivider,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import styled from "styled-components";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import EditModal from "./EditModal";
import ProductNode from "./nodes/ProductNode";
import MaterialNode from "./nodes/MaterialNode";
import SemiProductNode from "./nodes/SemiProductNode";
import { useParams } from "react-router-dom";
import PLAN_URL from "../../../utils/PLAN_URL";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  background-color: var(--mui-palette-background-default);
  box-sizing: border-box;
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
  background-color: var(--mui-palette-background-paper);
  border-radius: 8px 8px 0 0;
  margin-bottom: 0;
  flex-shrink: 0;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const SidePanel = styled(Paper)`
  width: 300px;
  flex-shrink: 0;
  overflow-y: auto;
  border-left: 1px solid var(--mui-palette-divider);
  border-radius: 0;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  position: relative;
  background-color: var(--mui-palette-background-paper);
  border-radius: 0 0 0 8px;
  box-shadow: var(--mui-shadows-2);
`;

const Title = styled(Typography)`
  color: var(--mui-palette-text-primary);
  margin: 0;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  max-width: 500px;
  margin: 0 24px;
`;

const getTreeLayout = (nodes, edges) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: "LR",
    nodesep: 80,
    ranksep: 150,
    marginx: 20,
    marginy: 20,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 250, height: 120 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 125,
        y: nodeWithPosition.y - 60,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

const getMultipleItemsLayout = (items, handleOpenEditModal) => {
  const allNodes = [];
  const allEdges = [];
  let yOffset = 0;
  const treeSpacing = 300;

  items.forEach((item, itemIndex) => {
    const treeNodes = [];
    const treeEdges = [];
    let nodeIdCounter = 0;

    // itemIndex를 포함하여 고유한 ID 생성
    const productNodeId = `product_${item.item_id}_tree${itemIndex}`;

    treeNodes.push({
      id: productNodeId,
      type: "productNode",
      data: {
        label: item.item_name,
        item_id: item.item_id,
        material_grouping: item.material_grouping,
        safety_stock: item.safety_stock,
        bomCount: item.subRows?.length || 0,
        onEdit: () => handleOpenEditModal(item),
      },
      position: { x: 0, y: 0 },
    });

    const subRows = item.main_bom || item.subRows || [];
    subRows.forEach((subItem) => {
      const isSemi = subItem.category === "semi" || subItem.item_type === 0;
      // itemIndex를 포함하여 고유한 ID 생성
      const nodeId = `${isSemi ? "semi" : "material"}_${
        subItem.material_id
      }_tree${itemIndex}_node${nodeIdCounter++}`;

      treeNodes.push({
        id: nodeId,
        type: isSemi ? "semiProductNode" : "materialNode",
        data: {
          label: subItem.item_name || subItem.material_name,
          material_id: subItem.material_id,
          quantity: subItem.quantity,
          qty_unit: subItem.qty_unit || subItem.default_unit,
          isSemi: isSemi,
        },
        position: { x: 0, y: 0 },
      });

      treeEdges.push({
        id: `edge_${productNodeId}_${nodeId}`,
        source: productNodeId,
        target: nodeId,
        type: "smoothstep",
        animated: false,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
        },
        label: `${subItem.quantity} ${
          subItem.qty_unit || subItem.default_unit || ""
        }`,
        labelStyle: { fill: "#666", fontWeight: 600, fontSize: 12 },
        labelBgStyle: { fill: "#fff", fillOpacity: 0.8 },
      });

      if (isSemi && subItem.subRows && subItem.subRows.length > 0) {
        subItem.subRows.forEach((nestedItem) => {
          const nestedIsSemi =
            nestedItem.category === "semi" || nestedItem.item_type === 0;
          // 중첩된 노드에도 고유한 ID 생성
          const nestedNodeId = `${nestedIsSemi ? "semi" : "material"}_${
            nestedItem.material_id
          }_tree${itemIndex}_node${nodeIdCounter++}`;

          treeNodes.push({
            id: nestedNodeId,
            type: nestedIsSemi ? "semiProductNode" : "materialNode",
            data: {
              label: nestedItem.item_name || nestedItem.material_name,
              material_id: nestedItem.material_id,
              quantity: nestedItem.quantity,
              qty_unit: nestedItem.qty_unit || nestedItem.default_unit,
              isSemi: nestedIsSemi,
            },
            position: { x: 0, y: 0 },
          });

          treeEdges.push({
            id: `edge_${nodeId}_${nestedNodeId}`,
            source: nodeId,
            target: nestedNodeId,
            type: "smoothstep",
            animated: false,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 20,
              height: 20,
            },
            label: `${nestedItem.quantity} ${
              nestedItem.qty_unit || nestedItem.default_unit || ""
            }`,
            labelStyle: { fill: "#666", fontWeight: 600, fontSize: 12 },
            labelBgStyle: { fill: "#fff", fillOpacity: 0.8 },
          });
        });
      }
    });

    const { nodes: layoutedTreeNodes, edges: layoutedTreeEdges } =
      getTreeLayout(treeNodes, treeEdges);

    const maxY = Math.max(
      ...layoutedTreeNodes.map((n) => n.position.y + 120),
      0
    );
    const treeHeight = maxY + 100;

    const offsetNodes = layoutedTreeNodes.map((node) => ({
      ...node,
      position: {
        x: node.position.x,
        y: node.position.y + yOffset,
      },
    }));

    allNodes.push(...offsetNodes);
    allEdges.push(...layoutedTreeEdges);

    yOffset += treeHeight + treeSpacing;
  });

  return { nodes: allNodes, edges: allEdges };
};

const nodeTypes = {
  productNode: ProductNode,
  semiProductNode: SemiProductNode,
  materialNode: MaterialNode,
};

const BomManagementContent = () => {
  const { group_code, project_code } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [semiProductMode, setSemiProductMode] = useState(false);
  const { setViewport, getViewport, fitView } = useReactFlow();
  const savedViewportRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const shouldFitViewRef = useRef(true);
  const openSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };
  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = semiProductMode ? "getSemiAll" : "getAll";
      const response = await axios.get(
        `${PLAN_URL}/api/bom/${endpoint}?group_code=${group_code}&project_code=${project_code}`
      );
      setItems(response.data || []);
      if (response.data && response.data.length > 0) {
        if (selectedItemIds.length === 0) {
          setSelectedItemIds([response.data[0].item_id]);
        } else {
          const validIds = selectedItemIds.filter((id) =>
            response.data.some((item) => item.item_id === id)
          );

          if (validIds.length === 0) {
            setSelectedItemIds([response.data[0].item_id]);
          } else if (validIds.length !== selectedItemIds.length) {
            setSelectedItemIds(validIds);
          }
        }
      }
    } catch (error) {
      console.error("Error loading BOM data:", error);
      openSnackbar(
        `데이터 로드 오류: ${error.response?.data?.message || error.message}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [group_code, project_code, semiProductMode, selectedItemIds]);
  const handleModeToggle = (event) => {
    const newMode = event.target.checked;
    setSemiProductMode(newMode);
    setSelectedItemIds([]);
    shouldFitViewRef.current = true;
    openSnackbar(
      `${newMode ? "반제품" : "완제품"} 모드로 전환되었습니다.`,
      "info"
    );
  };
  useEffect(() => {
    loadData();
  }, [loadData, updateStatus]);
  const handleOpenEditModal = useCallback(
    (item) => {
      savedViewportRef.current = getViewport();
      setSelectedItem({
        parent_item_id: item.item_id,
        parent_item_name: item.item_name,
        existingBomItems: item.main_bom || item.subRows || [],
      });
      setIsEditModalOpen(true);
    },
    [getViewport]
  );
  useEffect(() => {
    if (items.length === 0 || selectedItemIds.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }
    const selectedItems = items.filter((item) =>
      selectedItemIds.includes(item.item_id)
    );
    if (selectedItems.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }
    const { nodes: layoutedNodes, edges: layoutedEdges } =
      getMultipleItemsLayout(selectedItems, handleOpenEditModal);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    setTimeout(() => {
      if (isInitialLoadRef.current || shouldFitViewRef.current) {
        fitView({ padding: 0.15, duration: 400 });
        isInitialLoadRef.current = false;
        shouldFitViewRef.current = false;
      } else if (savedViewportRef.current) {
        setViewport(savedViewportRef.current, { duration: 0 });
      }
    }, 100);
  }, [
    items,
    selectedItemIds,
    handleOpenEditModal,
    setNodes,
    setEdges,
    fitView,
    setViewport,
  ]);
  const handleSearchChange = (e) => {
    setSearchKeyword(e.target.value);
  };
  const handleItemToggle = (itemId) => {
    setSelectedItemIds((prev) => {
      if (prev.includes(itemId)) {
        if (prev.length === 1) {
          return prev;
        }
        return prev.filter((id) => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
    shouldFitViewRef.current = true;
  };
  const handleCloseModal = () => {
    setIsEditModalOpen(false);
  };
  const handleSaveChanges = (result) => {
    if (result.success) {
      openSnackbar(result.message || "BOM이 업데이트되었습니다.", "success");
      shouldFitViewRef.current = false;
      setUpdateStatus((prev) => !prev);
    } else {
      openSnackbar(result.message || "업데이트에 실패했습니다.", "error");
    }
  };
  const filteredItems = items.filter((item) => {
    const keyword = searchKeyword.toLowerCase();
    const itemName = String(item.item_name || "").toLowerCase();
    const materialGrouping = String(item.material_grouping || "").toLowerCase();
    return itemName.includes(keyword) || materialGrouping.includes(keyword);
  });
  return (
    <PageContainer>
      <HeaderContainer>
        <Title variant="h4" sx={{ display: "flex", alignItems: "center" }}>
          {semiProductMode ? "반제품" : "완제품"} BOM 관리 &nbsp;
          <Tooltip title="도움말" sx={{ cursor: "pointer" }}>
            <HelpOutlineIcon />
          </Tooltip>
        </Title>
        <SearchContainer>
          <TextField
            fullWidth
            size="small"
            placeholder="검색 (제품명, Material Grouping)"
            value={searchKeyword}
            onChange={handleSearchChange}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </SearchContainer>
        <FormControlLabel
          control={
            <Switch
              checked={semiProductMode}
              onChange={handleModeToggle}
              color="primary"
            />
          }
          label={semiProductMode ? "반제품" : "완제품"}
        />
      </HeaderContainer>
      <MainContent>
        <ContentContainer>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : nodes.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                {selectedItemIds.length === 0
                  ? "왼쪽에서 항목을 선택하세요."
                  : "BOM 정보가 없습니다."}
              </Typography>
            </Box>
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              minZoom={0.1}
              maxZoom={1.5}
              attributionPosition="bottom-left"
            >
              <Background color="#aaa" gap={16} />
              <Controls />
              <MiniMap
                nodeStrokeWidth={3}
                zoomable
                pannable
                nodeColor={(node) => {
                  if (node.type === "productNode") return "#667eea";
                  if (node.type === "semiProductNode") return "#f093fb";
                  return "#a8edea";
                }}
                style={{
                  backgroundColor: "#f8f9fa",
                }}
              />
            </ReactFlow>
          )}
        </ContentContainer>
        <SidePanel elevation={0}>
          <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {semiProductMode ? "반제품" : "완제품"} 목록
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                총 {filteredItems.length}개
              </Typography>
              <Typography variant="caption" color="primary" fontWeight={600}>
                선택: {selectedItemIds.length}개
              </Typography>
            </Box>
          </Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : filteredItems.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {items.length === 0
                  ? "등록된 항목이 없습니다."
                  : "검색 결과가 없습니다."}
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {filteredItems.map((item, index) => (
                <div key={`item_${item.item_id}`}>
                  {" "}
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={selectedItemIds.includes(item.item_id)}
                      onClick={() => handleItemToggle(item.item_id)}
                      sx={{
                        borderLeft: selectedItemIds.includes(item.item_id)
                          ? "4px solid #1976d2"
                          : "4px solid transparent",
                        "&.Mui-selected": {
                          backgroundColor: "#e3f2fd",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "#bbdefb",
                        },
                      }}
                    >
                      <Checkbox
                        checked={selectedItemIds.includes(item.item_id)}
                        tabIndex={-1}
                        disableRipple
                        sx={{ mr: 1 }}
                      />
                      <ListItemText
                        primary={item.item_name}
                        secondary={
                          <div>
                            {item.material_grouping && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {item.material_grouping}
                              </Typography>
                            )}
                            <Chip
                              label={`BOM ${item.subRows?.length || 0}항목`}
                              size="small"
                              sx={{ mt: 0.5, height: 20, fontSize: "0.7rem" }}
                            />
                          </div>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < filteredItems.length - 1 && <MuiDivider />}
                </div>
              ))}
            </List>
          )}
        </SidePanel>
      </MainContent>
      {isEditModalOpen && (
        <EditModal
          open={isEditModalOpen}
          onClose={handleCloseModal}
          item={selectedItem}
          group_code={group_code}
          project_code={project_code}
          apiUrl={PLAN_URL}
          onSave={handleSaveChanges}
          semiProductMode={semiProductMode}
        />
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

function BomManagement() {
  return (
    <ReactFlowProvider>
      <BomManagementContent />
    </ReactFlowProvider>
  );
}

export default BomManagement;
